import { Header, Label } from '@/pages/infant/infant-profile/components';
import Infant from '@/assets/infant.svg';
import { DynamicFormProps } from '../../dynamic-form';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { replaceBraces, usePrevious } from '@ecdlink/core';
import {
  Alert,
  ButtonGroup,
  ButtonGroupTypes,
  Checkbox,
  Dialog,
  DialogPosition,
  FormInput,
  IMAGE_WIDTH,
  ImageInput,
  renderIcon,
  Typography,
} from '@ecdlink/ui';

import { useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import { MaternalRecordExample } from '@/pages/infant/components/infant-road-to-health/maternalRecordExampleDialog';
import { useStaticData } from '@/hooks/useStaticData';
import { FileTypeEnum, WorkflowStatusEnum } from '@ecdlink/graphql';
import { newGuid } from '@/utils/common/uuid.utils';
import { documentActions, documentThunkActions } from '@/store/document';
import { Document } from '@ecdlink/core/lib';
import { useSelector } from 'react-redux';
import { userSelectors } from '@/store/user';
import { useAppDispatch } from '@/store';
import { DocumentActions } from '@/store/document/document.actions';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { useRequestResponseDialog } from '@/hooks/useRequestResponseDialog';
import {
  infantRoadToHealthModelSchema,
  InfantRoadToHealthModel,
} from './infant-road-to-health';
import { InfantModelInput } from '@/../../../packages/graphql/lib';

const acceptedFormats = ['jpg', 'pdf', 'jpeg', 'png', 'heic', 'heif'];
const acceptedMimeFormats = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
];

export const RoadToHeathBookStep = ({
  infant,
  setInfantInput,
  setEnableButton,
  setSectionQuestions,
}: DynamicFormProps) => {
  const {
    watch,
    setValue,
    register,
    control: infantRoadToHealthControl,
    trigger,
    getValues,
  } = useForm<InfantRoadToHealthModel>({
    resolver: yupResolver(infantRoadToHealthModelSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const {
    weightAtBirth,
    lengthAtBirth,
    roadToHealthBook,
    notRoadToHealthBook,
  } = watch();

  const { errors, isValid } = useFormState({
    control: infantRoadToHealthControl,
  });

  const visitSection = 'Road to Health Book';

  const [hasMaternalCaseRecord, setHasMaternalCaseRecord] = useState<
    boolean | undefined
  >(undefined);
  const [photoActionBarVisible, setPhotoActionBarVisible] =
    useState<boolean>(false);
  const [maternalRecordExampleVisible, setMaternalRecordExampleVisible] =
    useState<boolean>(false);

  const name = useMemo(
    () => infant?.firstName ?? infant?.user?.firstName ?? '',
    [infant]
  );
  const question1 = "Does the caregiver have {client}'s Road to Health Book?";

  const previousWeight = usePrevious(weightAtBirth);
  const previousLength = usePrevious(lengthAtBirth);
  const previousRoadToHealthBook = usePrevious(roadToHealthBook);
  const previousHasMaternalCaseRecord = usePrevious(hasMaternalCaseRecord);
  const previousNotRoadToHealthBook = usePrevious(notRoadToHealthBook);
  const { getWorkflowStatusIdByEnum, getDocumentTypeIdByEnum } =
    useStaticData();

  const user = useSelector(userSelectors.getUser);

  const appDispatch = useAppDispatch();

  const { isRejected, isLoading } = useThunkFetchCall(
    'documents',
    DocumentActions.CREATE_DOCUMENT
  );

  const wasLoading = usePrevious(isLoading);

  const { errorDialog } = useRequestResponseDialog();

  const options = [
    { text: 'Yes', value: true },
    { text: 'No', value: false },
  ];

  const getFormattedWeight = (weight?: number) => {
    const formattedVal = weight?.toString().replaceAll(',', '.');
    return Math.trunc((Number(formattedVal) / 1000) * 10) / 10;
  };

  const onUpdateInfant = useCallback(async () => {
    if (infant?.user?.id) {
      const values = getValues();

      let input: InfantModelInput = {
        weightAtBirth: getFormattedWeight(
          Number(values?.weightAtBirth?.toString().replaceAll(',', '.'))
        ).toString(),
        lengthAtBirth: values?.lengthAtBirth,
        dateOfBirth: infant?.user?.dateOfBirth,
      };
      setInfantInput?.(input);
    }
  }, [getValues, infant?.user?.dateOfBirth, infant?.user?.id, setInfantInput]);

  const onSubmitDocument = useCallback(async () => {
    if (infant?.user?.id && roadToHealthBook) {
      const fileName = 'roadtohealthbook.png';
      const workflowStatusId = getWorkflowStatusIdByEnum(
        WorkflowStatusEnum.DocumentPendingVerification
      );
      const documentTypeId = getDocumentTypeIdByEnum(
        FileTypeEnum.RoadToHealthBook
      );
      const documentInputModel: Document = {
        id: newGuid(),
        userId: infant?.user.id,
        createdUserId: user?.id ?? '',
        workflowStatusId: workflowStatusId ?? '',
        documentTypeId: documentTypeId ?? '',
        name: fileName,
        fileName: fileName,
        file: roadToHealthBook,
        fileType: FileTypeEnum.RoadToHealthBook,
      };

      appDispatch(documentActions.createDocument(documentInputModel));
      await appDispatch(
        documentThunkActions.createDocument(documentInputModel)
      ).unwrap();
    }
  }, [
    appDispatch,
    getDocumentTypeIdByEnum,
    getWorkflowStatusIdByEnum,
    infant?.user?.id,
    roadToHealthBook,
    user?.id,
  ]);

  const onOptionSelected = useCallback(async () => {
    const isFormValid = await trigger();

    if (hasMaternalCaseRecord && !isFormValid) {
      setEnableButton?.(false);
      return;
    }

    const values = getValues();
    const wab = values.weightAtBirth
      ? getFormattedWeight(Number(values.weightAtBirth))
      : undefined;
    const lab = values.lengthAtBirth;

    if (
      previousLength === lab &&
      previousWeight === wab &&
      previousRoadToHealthBook === values.roadToHealthBook &&
      previousHasMaternalCaseRecord === hasMaternalCaseRecord &&
      previousNotRoadToHealthBook === values.notRoadToHealthBook
    ) {
      return;
    }

    setSectionQuestions?.([
      {
        visitSection,
        questions: [
          {
            question: question1,
            answer: String(hasMaternalCaseRecord),
          },
          {
            question: 'Weight at birth',
            answer: getFormattedWeight(
              Number(values?.weightAtBirth?.toString().replaceAll(',', '.'))
            ).toString(),
          },
          {
            question: 'Length at birth',
            answer: String(
              values.lengthAtBirth?.toString().replaceAll(',', '.')
            ).replaceAll(',', '.'),
          },
          {
            question: 'Take a photo of page ii of the Road to Health Book.',
            answer: values.roadToHealthBook || '',
          },
        ],
      },
    ]);

    const withDocument = !!values.roadToHealthBook;

    const isAllCompleted =
      (hasMaternalCaseRecord &&
        withDocument &&
        values.weightAtBirth &&
        values.lengthAtBirth &&
        isFormValid) ||
      (!hasMaternalCaseRecord && !!values.notRoadToHealthBook);

    setEnableButton?.(isAllCompleted || false);

    if (
      hasMaternalCaseRecord &&
      withDocument &&
      isFormValid &&
      values.weightAtBirth &&
      values.lengthAtBirth
    ) {
      onUpdateInfant();
    }

    if (
      values.roadToHealthBook &&
      previousRoadToHealthBook !== values.roadToHealthBook
    ) {
      onSubmitDocument();
    }
  }, [
    trigger,
    getValues,
    hasMaternalCaseRecord,
    previousLength,
    previousWeight,
    previousRoadToHealthBook,
    previousHasMaternalCaseRecord,
    previousNotRoadToHealthBook,
    setSectionQuestions,
    setEnableButton,
    onUpdateInfant,
    onSubmitDocument,
    visitSection,
    question1,
  ]);

  const handleWeightChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Updated type
      const value = event.target.value;
      setValue('weightAtBirth', value, {
        shouldValidate: true,
      });
      trigger('weightAtBirth').then(onOptionSelected);
    },
    [onOptionSelected, setValue, trigger]
  );

  const handleLengthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Updated type
      const value = event.target.value;
      setValue('lengthAtBirth', value, {
        shouldValidate: true,
      });
      trigger('lengthAtBirth').then(onOptionSelected);
    },
    [onOptionSelected, setValue, trigger]
  );

  useEffect(() => {
    if (wasLoading && isRejected) {
      errorDialog();
    }
  }, [errorDialog, isRejected, wasLoading]);

  return (
    <>
      <Header
        backgroundColor="tertiary"
        customIcon={Infant}
        title={visitSection}
        subTitle={name}
      />
      <div className="flex flex-col gap-4 p-4">
        <Label text={replaceBraces(question1, name || '')} />
        <ButtonGroup<boolean>
          color="secondary"
          type={ButtonGroupTypes.Button}
          options={options}
          onOptionSelected={(value) => {
            setHasMaternalCaseRecord(value as boolean);
            if (value === false) {
              setValue('roadToHealthBook', '', { shouldValidate: true });
            } else {
              setValue('notRoadToHealthBook', undefined, {
                shouldValidate: true,
              });
            }
            onOptionSelected();
          }}
        />

        {!!hasMaternalCaseRecord && (
          <>
            <div className="mt-4 flex items-center justify-between">
              <Typography
                type="h4"
                weight="bold"
                color={'textMid'}
                text={'Take a photo of page ii of the Road to Health Book.'}
                className="w-9/12 pt-2"
              />
              <button onClick={() => setMaternalRecordExampleVisible(true)}>
                {renderIcon('InformationCircleIcon', 'h-6 w-6 text-infoMain')}
              </button>
            </div>
            <ImageInput<InfantRoadToHealthModel>
              acceptedFormats={acceptedFormats}
              resolutionLimit={IMAGE_WIDTH}
              label={''}
              nameProp="roadToHealthBook"
              icon="CameraIcon"
              iconContainerColor={'tertiary'}
              className={'pt-1'}
              currentImageString={roadToHealthBook}
              overrideOnClick={() => setPhotoActionBarVisible(true)}
              register={register}
              onValueChange={(imageString: string) => {
                setValue('roadToHealthBook', imageString, {
                  shouldValidate: true,
                });
                trigger('roadToHealthBook').then(() => onOptionSelected());
              }}
            ></ImageInput>
            <div className="flex items-center gap-1">
              <FormInput<InfantRoadToHealthModel>
                label={'Weight at birth'}
                register={register}
                nameProp={'weightAtBirth'}
                placeholder={'Tap to add'}
                type={'text'}
                min={0}
                maxLength={9}
                className="mt-4"
                id={'weight'}
                error={
                  weightAtBirth && !!errors.weightAtBirth
                    ? errors.weightAtBirth
                    : undefined
                }
                onChange={handleWeightChange}
              />
              <Typography
                type="h4"
                color={'textMid'}
                text={'g'}
                className="mt-12"
              />
            </div>
            <div className="flex items-center gap-1">
              <FormInput<InfantRoadToHealthModel>
                label={'Length at birth'}
                register={register}
                nameProp={'lengthAtBirth'}
                placeholder={'Tap to add'}
                type={'text'}
                className="mt-4"
                min={0}
                maxLength={5}
                id={'length'}
                error={
                  lengthAtBirth && !!errors.lengthAtBirth
                    ? errors.lengthAtBirth
                    : undefined
                }
                onChange={handleLengthChange}
              ></FormInput>
              <Typography
                type="h4"
                color={'textMid'}
                text={'cm'}
                className="mt-12"
              />
            </div>
            {!!weightAtBirth &&
              Number(String(weightAtBirth).replaceAll(',', '.')) < 2.5 && (
                <Alert
                  type={'warning'}
                  message={'Low birth weight'}
                  className="mt-6"
                />
              )}
          </>
        )}
        {hasMaternalCaseRecord === false && (
          <>
            <Typography
              type="h2"
              color={'textDark'}
              text={'Please confirm'}
              className="pt-6"
            />
            <div className="flex items-start gap-2">
              <Checkbox
                checked={notRoadToHealthBook}
                onCheckboxChange={(value) => {
                  setValue('notRoadToHealthBook', value.checked, {
                    shouldValidate: true,
                  });
                  onOptionSelected();
                }}
              />
              <Typography
                text={`I do not have a copy of ${name}'s Road to Health Book. I declare that all information provided about ${name} is correct.`}
                type="body"
                color="textMid"
              />
            </div>
            <Alert
              type={'info'}
              message={`You will be required to upload ${name}'s document in a future visit.`}
            />
          </>
        )}
      </div>
      <Dialog
        visible={photoActionBarVisible}
        position={DialogPosition.Bottom}
        stretch
      >
        <PhotoPrompt
          acceptedMimeFormats={acceptedMimeFormats}
          title="Road to Health Book, page ii"
          resolutionLimit={IMAGE_WIDTH}
          hideEmojiOption
          onClose={() => setPhotoActionBarVisible(false)}
          onAction={(imageUrl: string) => {
            setValue('roadToHealthBook', imageUrl, { shouldValidate: true });
            setPhotoActionBarVisible(false);
            trigger('roadToHealthBook').then(() => onOptionSelected());
          }}
          {...(!!roadToHealthBook && {
            onDelete: () => {
              setValue('roadToHealthBook', '', { shouldValidate: true });
              setPhotoActionBarVisible(false);
              onOptionSelected();
            },
          })}
        ></PhotoPrompt>
      </Dialog>
      <MaternalRecordExample
        isVisible={maternalRecordExampleVisible}
        onClose={() => setMaternalRecordExampleVisible(false)}
      />
    </>
  );
};
